/**
 * Artwork Drawer — storage.
 *
 * Keeps the artworks a customer generated so they can come back to them later.
 * Pure data: no DOM, no knowledge of the personalizer or of the drawer UI.
 *
 * Hard rule: nothing in here may ever throw. It runs inside the artwork
 * generation flow, and a storage problem must never break a sale.
 */
(function () {
  'use strict';

  // Two features now want this store — the artwork drawer and the Poster
  // Builder — and each loads it for itself, because neither can assume the
  // other is on the page. Loading twice would register a second `storage`
  // listener and fire every change event twice.
  if (window.ArtworkDrawerStore) return;

  var KEY = 'artwork_drawer_v1';
  var VERSION = 1;

  // Overridden from the snippet config on first use; these are the fallbacks.
  var MAX = 10;
  var TTL_MS = 30 * 24 * 60 * 60 * 1000;

  // Mirror of what is on disk. Becomes the only store when localStorage is
  // unavailable (private mode, blocked cookies, quota exhausted).
  var memory = null;
  var lsOk = probe();
  var warned = false;
  var prunedThisLoad = false;

  function probe() {
    try {
      var k = '__awd_probe__';
      window.localStorage.setItem(k, '1');
      window.localStorage.removeItem(k);
      return true;
    } catch (e) {
      return false;
    }
  }

  function degrade(items) {
    memory = items || [];
    lsOk = false;
    if (!warned && window.console) {
      warned = true;
      console.warn('[artwork-drawer] storage unavailable, keeping artworks in memory only');
    }
  }

  // ---- Validation ------------------------------------------------------------

  // Entries come back from localStorage, which the customer can edit. Every
  // field is re-checked on read, and only https URLs are ever accepted.
  function safeUrl(u) {
    return typeof u === 'string' && u.indexOf('https://') === 0 ? u : null;
  }

  function safeNum(n) {
    if (n === null || n === undefined || n === '') return null;
    var v = Number(n);
    return isFinite(v) ? v : null;
  }

  function safePlacement(p) {
    if (!p || typeof p !== 'object') return null;
    return {
      scale: safeNum(p.scale) || 0.85,
      xPct: safeNum(p.xPct) === null ? 50 : Number(p.xPct),
      yPct: safeNum(p.yPct) === null ? 50 : Number(p.yPct)
    };
  }

  function normalize(e) {
    if (!e || typeof e !== 'object') return null;
    var thumb = safeUrl(e.thumbUrl);
    if (!e.id || !e.handle || !thumb) return null; // required

    return {
      id: String(e.id),
      createdAt: safeNum(e.createdAt) || Date.now(),
      mode: e.mode === 'background' ? 'background' : 'plain',
      productId: String(e.productId || ''),
      handle: String(e.handle),
      styleLabel: String(e.styleLabel || '').slice(0, 40),
      productTitle: String(e.productTitle || '').slice(0, 160),
      thumbUrl: thumb,
      printUrl: safeUrl(e.printUrl),
      previewUrl: safeUrl(e.previewUrl),
      originalUrl: safeUrl(e.originalUrl),
      backgroundId: e.backgroundId ? String(e.backgroundId) : null,
      bgThumbUrl: safeUrl(e.bgThumbUrl),
      // Kept for the lifetime of the entry: composing overwrites thumbUrl, and
      // this is what we fall back to if the customer moves the subject again.
      subjectUrl: safeUrl(e.subjectUrl),
      placement: safePlacement(e.placement),
      subjectW: safeNum(e.subjectW),
      subjectH: safeNum(e.subjectH),
      composedAt: safeNum(e.composedAt)
    };
  }

  // ---- Read / write ----------------------------------------------------------

  function hardReset() {
    try {
      window.localStorage.removeItem(KEY);
    } catch (e) {
      /* nothing else to do */
    }
  }

  function prune(items) {
    var cutoff = Date.now() - TTL_MS;
    return items
      .filter(function (x) {
        return x.createdAt > cutoff;
      })
      .sort(function (a, b) {
        return b.createdAt - a.createdAt;
      })
      .slice(0, MAX);
  }

  function read() {
    if (!lsOk) return memory || (memory = []);

    var raw;
    try {
      raw = window.localStorage.getItem(KEY);
    } catch (e) {
      degrade([]);
      return memory;
    }
    if (!raw) return [];

    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      hardReset();
      return [];
    }
    if (!parsed || parsed.v !== VERSION || !Array.isArray(parsed.items)) {
      hardReset();
      return [];
    }

    var clean = [];
    for (var i = 0; i < parsed.items.length; i++) {
      var entry = normalize(parsed.items[i]);
      if (entry) clean.push(entry);
    }

    var kept = prune(clean);
    // Expired entries should actually leave storage, but a plain read must not
    // cause a write on every call. Once per page load is enough.
    if (!prunedThisLoad && kept.length !== parsed.items.length) {
      prunedThisLoad = true;
      write(kept, true);
    }
    return kept;
  }

  function write(items, silent) {
    memory = items;
    if (!lsOk) return;

    var list = items.slice();
    for (var attempt = 0; attempt < 4; attempt++) {
      try {
        window.localStorage.setItem(KEY, JSON.stringify({ v: VERSION, items: list }));
        if (!silent) emit(list.length);
        return;
      } catch (err) {
        // Quota exceeded: shed the oldest artwork and try again. Some other
        // script on the page may be filling storage, so give up gracefully.
        if (list.length > 1) {
          list.pop();
          continue;
        }
        hardReset();
        degrade(items);
        if (!silent) emit(items.length);
        return;
      }
    }
  }

  function emit(count) {
    try {
      document.dispatchEvent(
        new CustomEvent('artwork-drawer:changed', { detail: { count: count } })
      );
    } catch (e) {
      /* ignore */
    }
  }

  // ---- Public API ------------------------------------------------------------

  function configure(opts) {
    if (!opts) return;
    var max = Number(opts.max);
    var ttl = Number(opts.ttlDays);
    if (isFinite(max) && max > 0) MAX = Math.min(50, Math.round(max));
    if (isFinite(ttl) && ttl > 0) TTL_MS = Math.round(ttl) * 24 * 60 * 60 * 1000;
  }

  function list() {
    try {
      return read();
    } catch (e) {
      return [];
    }
  }

  function get(id) {
    var items = list();
    for (var i = 0; i < items.length; i++) {
      if (items[i].id === String(id)) return items[i];
    }
    return null;
  }

  function save(partial) {
    try {
      var entry = normalize(partial);
      if (!entry) return null;

      var items = list().filter(function (x) {
        return x.id !== entry.id;
      });
      items.unshift(entry);
      write(prune(items));
      return entry;
    } catch (e) {
      return null;
    }
  }

  // Patch an existing entry in place. Order is preserved on purpose: an update
  // (a background artwork being composed, say) must not jump the artwork back
  // to the top of the gallery.
  function update(id, patch) {
    try {
      var items = list();
      var key = String(id);
      for (var i = 0; i < items.length; i++) {
        if (items[i].id !== key) continue;

        var merged = normalize(Object.assign({}, items[i], patch || {}));
        if (!merged) return null;
        merged.createdAt = items[i].createdAt;
        items[i] = merged;
        write(items);
        return merged;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  function remove(id) {
    try {
      var key = String(id);
      var items = list();
      var kept = items.filter(function (x) {
        return x.id !== key;
      });
      if (kept.length === items.length) return false;
      write(kept);
      return true;
    } catch (e) {
      return false;
    }
  }

  function count() {
    return list().length;
  }

  // Another tab changed the artworks: refresh whatever is on screen here.
  try {
    window.addEventListener('storage', function (e) {
      if (e && e.key === KEY) emit(count());
    });
  } catch (e) {
    /* ignore */
  }

  // The merchant's "keep artworks for N days" and "keep at most N" settings.
  // The artwork drawer used to read them off its own root element and call
  // configure(); it no longer runs, so the tab snippet publishes them as a
  // global instead and this picks them up whenever the module happens to load.
  // Order-independent by construction: the global is written by inline HTML.
  if (window.ArtworkDrawerStoreConfig) configure(window.ArtworkDrawerStoreConfig);

  window.ArtworkDrawerStore = {
    configure: configure,
    save: save,
    update: update,
    get: get,
    list: list,
    remove: remove,
    count: count,
    get max() {
      return MAX;
    }
  };
})();
